import { useEffect } from 'react'
import { radius, theme } from '../theme'

/**
 * The letter keyboard the word games share.
 *
 * On screen rather than the device's own, for three reasons: a tablet's
 * keyboard covers half the puzzle, it offers autocorrect (which would solve
 * the crossword for you), and it cannot colour its keys. A real keyboard
 * works too, for anyone on a laptop.
 *
 * `marks` colours individual keys — the mystery word uses it to show which
 * letters are in the word, in the wrong place, or not in it at all.
 */
export type KeyMark = 'right' | 'near' | 'out'

export const MARK_COLORS: Record<KeyMark, { bg: string; ink: string; label: string }> = {
  right: { bg: '#3E9B4F', ink: '#FFFFFF', label: 'in the right place' },
  near: { bg: '#E0A82E', ink: '#FFFFFF', label: 'in the word, wrong place' },
  out: { bg: '#A7B0BA', ink: '#FFFFFF', label: 'not in the word' },
}

const ROWS = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM']

export function LetterKeyboard({
  onLetter, onBack, onEnter, marks, enterLabel = 'Enter', disabled,
}: {
  onLetter: (letter: string) => void
  /** Left out where there is nothing to delete (hangman): no ⌫ key then. */
  onBack?: () => void
  onEnter?: () => void
  marks?: Record<string, KeyMark>
  enterLabel?: string
  disabled?: boolean
}) {
  // The real keyboard. Ignored while typing into some other box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled || e.ctrlKey || e.metaKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
      if (e.key === 'Backspace' && onBack) { e.preventDefault(); onBack() }
      else if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter() }
      else if (/^[a-zA-Z]$/.test(e.key)) onLetter(e.key.toUpperCase())
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onLetter, onBack, onEnter, disabled])

  const key = (label: string, onClick: () => void, wide = false, aria?: string) => {
    const mark = label.length === 1 ? marks?.[label] : undefined
    const colors = mark ? MARK_COLORS[mark] : null
    return (
      <button
        key={label}
        type="button"
        disabled={disabled}
        onClick={onClick}
        aria-label={aria ?? (colors ? `${label}, ${colors.label}` : label)}
        style={{
          flex: wide ? '1.6 1 0' : '1 1 0',
          minWidth: 0,
          height: 50,
          padding: 0,
          borderRadius: radius.sm,
          border: 'none',
          background: colors ? colors.bg : theme.sand,
          color: colors ? colors.ink : theme.text,
          fontFamily: 'inherit',
          fontSize: wide ? 14 : 18,
          fontWeight: 800,
          cursor: disabled ? 'default' : 'pointer',
          touchAction: 'manipulation',
        }}
      >
        {label}
      </button>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, userSelect: 'none' }}>
      {ROWS.map((row, i) => (
        <div key={row} style={{ display: 'flex', gap: 5, padding: i === 1 ? '0 4%' : 0 }}>
          {i === 2 && onEnter ? key(enterLabel, onEnter, true) : null}
          {[...row].map((l) => key(l, () => onLetter(l)))}
          {i === 2 && onBack ? key('⌫', onBack, true, 'Delete a letter') : null}
        </div>
      ))}
    </div>
  )
}
